import { mtstatTrack } from '$/utils/mtstat';
import { getUrlSearchParams } from '$/utils';
import useFlowStore from '$/store/useFlowStore';
import useTeamFlowStore from '$/store/useTeamFlowStore';
import type { OfficialAssetServerMaterial } from '$/api/server.types';
import {
  resolveTeamTrackingContext,
  trackTeamHistoryCardClick as trackTeamHistoryCardClickEvent,
  trackTeamHistoryCardShow as trackTeamHistoryCardShowEvent,
  type TeamHistoryCardMediaType,
} from '$/domains/workFlowV3/teamTracking';
import type {
  AssetItemView,
  AssetModalInsertTarget,
  OfficialAssetPopupTrackLocation,
  RoboneoAssetUploadClickLocation,
} from './type';
import type { AssetLibraryTabKey } from './AssetLibraryModalContent/tabs';
import type {
  RoleGenFormValues,
  RoleGenMaterialCategory,
  RoleGenMaterialSource,
} from './AssetLibraryModalContent/CreateRoleModal/RoleSettingForm';
import type { ThreeViewImage } from './AssetLibraryModalContent/CreateRoleModal/useRoleGenSubmit';

/**
 * 吊起资产库弹窗的入口点击埋点：点击一次上报一次。
 *
 * 独立于 utils.ts，避免仅为埋点函数而静态引入 canvas / workflow store 依赖。
 *
 * @param location 入口场景（首页输入框 / 工作流底部 / Agent 画布底部 / Agent Teams 底部等）
 */
export function trackRoboneoAssetUploadClick(location: RoboneoAssetUploadClickLocation): void {
  mtstatTrack('roboneo_asset_upload_click', { location });
}

/** 解析资产库相关埋点 room_id，保持与弹窗插入目标一致。 */
export function resolveAssetModalTrackRoomId(insertTarget?: AssetModalInsertTarget): string {
  if (insertTarget === 'workflow') {
    return useFlowStore.getState().roomId || '';
  }
  if (insertTarget === 'agentTeam') {
    return useTeamFlowStore.getState().roomId || '';
  }
  return getUrlSearchParams().get('room_id') || '';
}

export type TeamAssetHistorySaveLocation = 'team_asset' | 'team_history';
export type TeamAssetHistorySaveEvent =
  | 'canvas_save_click'
  | 'canvas_save_success'
  | 'canvas_save_fail';

/** 从素材原始字段读取下载埋点 task_id，保持与公共预览链路一致。 */
function getAssetTrackTaskId(item: AssetItemView): string {
  const raw = item.raw as Record<string, any>;
  return String(raw.task_id || raw.source_id || raw.id || item.id || '');
}

/** 组装团队资产或历史下载的公共上下文参数。 */
export function buildTeamAssetHistorySaveStatParams(
  item: AssetItemView,
  location: TeamAssetHistorySaveLocation,
): Record<string, string> {
  const context = resolveTeamTrackingContext({
    roomId: item.raw.room_id || resolveAssetModalTrackRoomId(),
  });
  if (!context.oid) return {};
  return {
    project_id: context.project_id,
    oid: context.oid,
    room_id: context.room_id,
    url: (item.mediaUrl || item.coverUrl || '').trim(),
    task_id: getAssetTrackTaskId(item),
    location,
  };
}

/** 上报团队资产或历史单条、批量下载的点击与结果事件。 */
export function trackTeamAssetHistorySave(
  eventName: TeamAssetHistorySaveEvent,
  items: AssetItemView[],
  location: TeamAssetHistorySaveLocation,
): void {
  if (!items.length) return;
  const firstItemParams = buildTeamAssetHistorySaveStatParams(items[0]!, location);
  if (!firstItemParams.oid) return;
  mtstatTrack(eventName, {
    ...firstItemParams,
    url: items
      .map((item) => (item.mediaUrl || item.coverUrl || '').trim())
      .filter(Boolean)
      .join(','),
    task_id: items.map(getAssetTrackTaskId).filter(Boolean).join(','),
  });
}

/** 上报资产库顶层 tab 点击。 */
export function trackRoboneoAssetTabClick(tabName: AssetLibraryTabKey): void {
  mtstatTrack('roboneo_asset_tab_click', { tab_name: tabName });
}

interface OfficialAssetTrackContext {
  roomId: string;
  assetType: string;
  keyword: string;
  searchValue: string;
}

interface OfficialAssetApplyTrackOptions {
  material: OfficialAssetServerMaterial;
  context: OfficialAssetTrackContext;
  positionId: number;
  clickType: 'merge' | 'split';
  mediaUrl: string;
}

/** 官方资产库弹框曝光：首次默认类型和每次类型切换分别上报。 */
export function trackRoboneoAssetOfficialPopShow({
  roomId,
  assetType,
  location,
}: {
  roomId: string;
  assetType: string;
  location: OfficialAssetPopupTrackLocation;
}): void {
  mtstatTrack('roboneo_asset_official_pop_show', {
    room_id: roomId,
    asset_type: assetType,
    location,
  });
}

/** 组装官方资产库卡片曝光、点击、应用和搜索公共参数。 */
function buildOfficialAssetTrackParams(
  material: OfficialAssetServerMaterial,
  context: OfficialAssetTrackContext,
  positionId: number,
) {
  return {
    room_id: context.roomId,
    asset_type: context.assetType,
    keyword: context.keyword,
    search_value: context.searchValue,
    official_id: String(material.id),
    position_id: String(positionId),
  };
}

/** 官方资产库卡片曝光：列表中出现一个卡片上报一次。 */
export function trackRoboneoAssetOfficialCardShow(
  material: OfficialAssetServerMaterial,
  context: OfficialAssetTrackContext,
  positionId: number,
): void {
  mtstatTrack(
    'roboneo_asset_official_card_show',
    buildOfficialAssetTrackParams(material, context, positionId),
  );
}

/** 官方资产库卡片点击：用户点击卡片进入详情时上报。 */
export function trackRoboneoAssetOfficialCardClick(
  material: OfficialAssetServerMaterial,
  context: OfficialAssetTrackContext,
  positionId: number,
): void {
  mtstatTrack('roboneo_asset_official_card_click', {
    ...buildOfficialAssetTrackParams(material, context, positionId),
    click_type: 'card_click',
  });
}

/** 官方资产库应用到画布：区分直接应用和合并后应用。 */
export function trackRoboneoAssetOfficialApplyClick({
  material,
  context,
  positionId,
  clickType,
  mediaUrl,
}: OfficialAssetApplyTrackOptions): void {
  mtstatTrack('roboneo_asset_official_apply_click', {
    ...buildOfficialAssetTrackParams(material, context, positionId),
    click_type: clickType,
    media_url: mediaUrl,
  });
}

/** 官方资产库搜索入口提交时上报当前关键词和筛选项。 */
export function trackRoboneoAssetOfficialSearchClick(context: OfficialAssetTrackContext): void {
  mtstatTrack('roboneo_asset_official_search_click', {
    room_id: context.roomId,
    asset_type: context.assetType,
    keyword: context.keyword,
    search_value: context.searchValue,
  });
}

/** 官方资产库搜索或筛选结果返回后上报结果数量。 */
export function trackRoboneoAssetOfficialSearchResult(
  context: OfficialAssetTrackContext,
  resultNum: number,
): void {
  mtstatTrack('roboneo_asset_official_search_result', {
    room_id: context.roomId,
    asset_type: context.assetType,
    keyword: context.keyword,
    search_value: context.searchValue,
    result_num: String(resultNum),
  });
}

/** 官方资产库素材落到画布后补充节点创建埋点。 */
export function trackRoboneoOfficialAssetNodeCreate({
  roomId,
  nodeId,
  contentUrl,
  materialId,
}: {
  roomId: string;
  nodeId: string;
  contentUrl: string;
  materialId: string;
}): void {
  mtstatTrack('roboneo_node_create', {
    room_id: roomId,
    node_source: 'official_card',
    content_source: 'official_card',
    task_id: '',
    content_type: 'image',
    content_urls: contentUrl,
    node_id: nodeId,
    before_node_id: '',
    fast_start_name: '',
    model_name: '',
    material_id: materialId,
  });
}

export type RoleAssetUploadLocation =
  | 'role_reference'
  | 'role_clothing'
  | 'role_hair_style'
  | 'role_accessory';
export type RoleAssetUploadTriggerType = 'click' | 'drop';

/** 将创建角色素材类型转换成上传埋点 location。 */
export function roleGenMaterialCategoryToUploadLocation(
  category: RoleGenMaterialCategory,
): RoleAssetUploadLocation {
  if (category === 'clothing') return 'role_clothing';
  if (category === 'hairstyle') return 'role_hair_style';
  return 'role_accessory';
}

/** 创建角色上传控件点击或拖拽投放时上报。 */
export function trackRoboneoAssetRoleUploadControlClick({
  roomId,
  location,
  triggerType,
}: {
  roomId: string;
  location: RoleAssetUploadLocation;
  triggerType: RoleAssetUploadTriggerType;
}): void {
  mtstatTrack('upload_control_click', {
    room_id: roomId,
    location,
    media_type: 'image',
    trigger_type: triggerType,
  });
}

/** 创建角色弹窗曝光：每次打开弹窗上报一次。 */
export function trackRoboneoAssetRoleShow(roomId: string): void {
  mtstatTrack('roboneo_asset_role_show', { room_id: roomId });
}

/** 将角色素材来源转换为大禹文档枚举，官方资产选择按资产库来源处理。 */
function normalizeRoleMaterialTrackSource(source?: RoleGenMaterialSource): string {
  if (source === 'official') return 'asset';
  return source || '';
}

/** 组装创建角色执行链路公共参数。 */
function buildRoleGenBaseTrackParams(values: RoleGenFormValues) {
  return {
    role_name: values.name.trim(),
    role_desc: values.prompt.trim(),
    role_reference_url: values.referenceImageUrl || '',
    role_clothing_url: values.clothingMaterialCoverUrl || '',
    role_scene_url: values.hairstyleMaterialCoverUrl || '',
    role_accessory_url: values.accessoryMaterialCoverUrl || '',
    role_reference_from: values.referenceImageSource || '',
    role_clothing_from: normalizeRoleMaterialTrackSource(values.clothingMaterialSource),
    role_scene_from: normalizeRoleMaterialTrackSource(values.hairstyleMaterialSource),
    role_accessory_from: normalizeRoleMaterialTrackSource(values.accessoryMaterialSource),
  };
}

/** 读取创建角色输入 URL 列表，按文档用英文逗号拼接。 */
function buildRoleGenInputUrls(values: RoleGenFormValues): string {
  return [
    values.referenceImageUrl,
    values.clothingMaterialCoverUrl,
    values.hairstyleMaterialCoverUrl,
    values.accessoryMaterialCoverUrl,
  ].filter((url): url is string => Boolean(url?.trim())).join(',');
}

/** 安全序列化创建角色输入参数，异常时按空字符串上报。 */
function stringifyRoleGenInputParams(inputParams?: Record<string, unknown>): string {
  if (!inputParams) return '';
  try {
    return JSON.stringify(inputParams);
  } catch {
    return '';
  }
}

/** 创建角色执行任务创建成功后上报，带上服务端返回 task_id。 */
export function trackRoboneoAssetRoleRunClick({
  roomId,
  values,
  cost,
  taskId,
}: {
  roomId: string;
  values: RoleGenFormValues;
  cost: string;
  taskId: string;
}): void {
  mtstatTrack('roboneo_asset_role_run_click', {
    room_id: roomId,
    ...buildRoleGenBaseTrackParams(values),
    cost,
    task_id: taskId,
  });
}

/** 创建角色生成成功后上报输入、结果和 task_id。 */
export function trackRoboneoAssetRoleRunSuccess({
  roomId,
  values,
  resultImages,
  taskId,
  inputParams,
}: {
  roomId: string;
  values: RoleGenFormValues;
  resultImages: ThreeViewImage[];
  taskId: string;
  inputParams?: Record<string, unknown>;
}): void {
  mtstatTrack('roboneo_asset_role_run_success', {
    room_id: roomId,
    role_name: values.name.trim(),
    role_desc: values.prompt.trim(),
    input_urls: buildRoleGenInputUrls(values),
    result_url: resultImages.map((image) => image.url).join(','),
    task_id: taskId,
    input_params: stringifyRoleGenInputParams(inputParams),
  });
}

/** 创建角色生成失败后上报输入、错误信息和 task_id。 */
export function trackRoboneoAssetRoleRunFail({
  roomId,
  values,
  taskId,
  inputParams,
  failInfo,
}: {
  roomId: string;
  values: RoleGenFormValues;
  taskId: string;
  inputParams?: Record<string, unknown>;
  failInfo: string;
}): void {
  mtstatTrack('roboneo_asset_role_run_fail', {
    room_id: roomId,
    role_name: values.name.trim(),
    role_desc: values.prompt.trim(),
    input_urls: buildRoleGenInputUrls(values),
    result_url: '',
    task_id: taskId,
    input_params: stringifyRoleGenInputParams(inputParams),
    fail_info: failInfo,
  });
}

/** 创建角色历史结果菜单曝光，上报当前任务和首张缩略图。 */
export function trackRoboneoAssetRoleHoverShow({
  roomId,
  taskId,
  mediaUrl,
  roleName,
}: {
  roomId: string;
  taskId: string;
  mediaUrl: string;
  roleName: string;
}): void {
  mtstatTrack('roboneo_asset_role_hover_show', {
    room_id: roomId,
    task_id: taskId,
    media_url: mediaUrl,
    role_name: roleName,
  });
}

/** 创建角色历史结果菜单按钮点击，区分重新编辑、删除和下载保存。 */
export function trackRoboneoAssetRoleHoverClick({
  roomId,
  taskId,
  mediaUrl,
  roleName,
  clickType,
}: {
  roomId: string;
  taskId: string;
  mediaUrl: string;
  roleName: string;
  clickType: 'edit' | 'delete' | 'save';
}): void {
  mtstatTrack('roboneo_asset_role_hover_click', {
    room_id: roomId,
    task_id: taskId,
    media_url: mediaUrl,
    role_name: roleName,
    click_type: clickType,
  });
}

interface OfficialRoleSaveAssetTrackOptions {
  roomId: string;
  taskId: string;
  mediaUrl: string;
}

const OFFICIAL_ROLE_SAVE_ASSET_SOURCE = 'official_role';
const OFFICIAL_ROLE_SAVE_ASSET_LOCATION = 'official_asset';

/** 组装官方角色结果保存资产埋点参数，非画布节点场景 node_id 按空值上报。 */
function buildOfficialRoleSaveAssetTrackParams({
  roomId,
  taskId,
  mediaUrl,
}: OfficialRoleSaveAssetTrackOptions) {
  return {
    room_id: roomId,
    node_id: '',
    node_type: 'IMAGE_NODE',
    content_type: 'IMAGE',
    media_url: mediaUrl,
    task_id: taskId,
    sub_task_id: '',
    duration: '',
    source_from: OFFICIAL_ROLE_SAVE_ASSET_SOURCE,
  };
}

/** 官方角色结果图保存资产入口点击，点击一次上报一次。 */
export function trackRoboneoAssetRoleSaveEnterClick(options: OfficialRoleSaveAssetTrackOptions): void {
  mtstatTrack('roboneo_save_enter_click', buildOfficialRoleSaveAssetTrackParams(options));
}

/** 官方角色结果图保存资产提交，弹窗点保存一次上报一次。 */
export function trackRoboneoAssetRoleSaveUpload(options: OfficialRoleSaveAssetTrackOptions): void {
  mtstatTrack('roboneo_save_asset_upload', {
    ...buildOfficialRoleSaveAssetTrackParams(options),
    location: OFFICIAL_ROLE_SAVE_ASSET_LOCATION,
  });
}

/** 创建角色结果存为资产完成或失败时补充 canvas_save_* 埋点。 */
export function trackRoboneoAssetRoleSaveResult({
  success,
  roomId,
  taskId,
  mediaUrl,
  assetId,
}: {
  success: boolean;
  roomId: string;
  taskId: string;
  mediaUrl: string;
  assetId?: string;
}): void {
  mtstatTrack(success ? 'canvas_save_success' : 'canvas_save_fail', {
    location: 'official_role',
    room_id: roomId,
    video_duration: '',
    save_type: 'image',
    url: mediaUrl,
    task_id: taskId,
    asset_id: assetId || '',
    audio_num: '0',
    image_num: mediaUrl ? '1' : '0',
  });
}

type HistoryActionKind = 'single' | 'bath';
type HistoryDeleteKind = HistoryActionKind | 'folder';

/** 将历史记录素材类型转换为大禹约定的 content_type，文档枚举沿用需求中的 docment。 */
function getHistoryContentType(item: AssetItemView): 'image' | 'video' | 'audio' | 'docment' {
  const materialType = (item.materialType || '').trim().toLowerCase();
  if (materialType === 'video') return 'video';
  if (materialType === 'audio') return 'audio';
  if (materialType === 'image') return 'image';
  return 'docment';
}

/** 读取历史记录操作埋点使用的原始内容地址。 */
function getHistoryContentUrl(item: AssetItemView): string {
  return (item.mediaUrl || item.raw.url || item.coverUrl || '').trim();
}

/** 归一化历史记录文件格式，优先使用列表映射后的扩展名。 */
function getHistoryFileFormat(item: AssetItemView): string {
  const rawFormat = item.fileExtension
    || item.raw.file?.format
    || item.raw.image?.format
    || item.raw.video?.format_name
    || item.raw.audio?.format_name
    || '';
  return String(rawFormat).trim().toLowerCase().replace(/^\./, '');
}

/** 将历史记录任务来源映射为收藏事件约定的 scene。 */
function getHistoryCollectScene(item: AssetItemView): string {
  const taskType = String(item.raw.task_type || item.raw.source_type || '').trim();
  if (taskType === 'workflow') return 'workflow';
  if (taskType === 'agent' || taskType === 'canvas') return 'agent';
  if (taskType === 'tool') return 'tools';
  if (taskType === 'agent-team') return 'agent_teams';
  return taskType || 'workflow';
}

/** 判断历史记录条目是否属于文本或办公文档，用于下载成功事件的 text_num。 */
function isHistoryTextContent(item: AssetItemView): boolean {
  const materialType = (item.materialType || '').trim().toLowerCase();
  return ['text', 'script', 'markdown', 'document', 'excel', 'ppt'].includes(materialType);
}

/** 将历史记录视频或音频秒级时长转换为大禹要求的毫秒字符串。 */
function getHistoryDurationMilliseconds(item: AssetItemView): string {
  const seconds = Number(item.raw.duration ?? item.raw.video?.duration ?? item.raw.audio?.duration);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  return String(Math.round(seconds * 1000));
}

interface TeamHistoryCardTrackOptions {
  item: AssetItemView;
  positionId: number;
  tabId: string;
  tabName: string;
  projectId?: string | number | null;
}

/** 将历史记录素材类型转换为团队卡片事件约定的枚举。 */
function getTeamHistoryMediaType(item: AssetItemView): TeamHistoryCardMediaType {
  if (item.materialType === 'image') return 'image';
  if (item.materialType === 'video') return 'video';
  if (item.materialType === 'audio') return 'audio';
  return 'file';
}

/** 组装团队历史卡片曝光与点击共用参数。 */
function buildTeamHistoryCardTrackOptions({
  item,
  positionId,
  tabId,
  tabName,
  projectId,
}: TeamHistoryCardTrackOptions) {
  const raw = item.raw as Record<string, any>;
  return {
    projectId,
    roomId: raw.room_id || resolveAssetModalTrackRoomId(),
    cardId: item.id,
    positionId,
    tabId,
    tabName,
    nodeName: raw.node_name || raw.name || raw.room_name || '',
    nodeNum: raw.node_num || raw.node_count || 1,
    authorId: raw.created_by_uid || '',
    mediaType: getTeamHistoryMediaType(item),
    mediaUrl: getHistoryContentUrl(item),
  };
}

/** 团队历史卡片首次出现在当前筛选结果中时上报曝光。 */
export function trackTeamHistoryCardShow(options: TeamHistoryCardTrackOptions): void {
  trackTeamHistoryCardShowEvent(buildTeamHistoryCardTrackOptions(options));
}

/** 用户点击团队历史卡片时上报。 */
export function trackTeamHistoryCardClick(options: TeamHistoryCardTrackOptions): void {
  trackTeamHistoryCardClickEvent(buildTeamHistoryCardTrackOptions(options));
}

/** 组装历史记录收藏或取消收藏事件参数，多条字段按选择顺序用英文逗号拼接。 */
function buildRoboneoHistoryCollectPayload(
  items: AssetItemView[],
  kind: HistoryActionKind,
): Record<string, string> {
  return {
    content_type: items.map(getHistoryContentType).join(','),
    content_url: items.map(getHistoryContentUrl).join(','),
    format: items.map(getHistoryFileFormat).join(','),
    scene: items.map(getHistoryCollectScene).join(','),
    type: kind,
  };
}

/** 组装历史记录内容删除事件参数，保留平台约定的 bath 拼写。 */
function buildRoboneoHistoryContentDeletePayload(
  items: AssetItemView[],
  kind: HistoryDeleteKind,
): Record<string, string> {
  if (kind === 'folder') {
    return { type: 'folder', content_type: '', content_url: '' };
  }
  return {
    type: kind,
    content_type: items.map(getHistoryContentType).join(','),
    content_url: items.map(getHistoryContentUrl).join(','),
  };
}

/** 组装历史记录详情页下载成功需要补充到 canvas_save_success 的业务参数。 */
export function buildRoboneoHistoryPreviewSaveStatParams(item: AssetItemView): Record<string, string> {
  const isVideoOrAudio = item.materialType === 'video' || item.materialType === 'audio';
  return {
    room_id: String(item.raw.room_id || ''),
    video_duration: getHistoryDurationMilliseconds(item),
    text_num: isHistoryTextContent(item) ? '1' : '0',
    file_type: getHistoryFileFormat(item),
    video_num: isVideoOrAudio ? '1' : '0',
  };
}

/** 上报资产库批量编辑栏曝光，仅由调用方在管理批量模式首次打开时触发。 */
export function trackRoboneoAssetBatchBarShow(folderName: string): void {
  const context = resolveTeamTrackingContext();
  mtstatTrack('roboneo_asset_batch_bar_show', {
    project_id: context.project_id,
    oid: context.oid,
    folder_name: folderName,
  });
}

/** 上报历史记录批量编辑栏曝光，仅由调用方在批量模式首次打开时触发。 */
export function trackRoboneoHistoryBatchBarShow(folderName: string): void {
  const context = resolveTeamTrackingContext();
  mtstatTrack('roboneo_history_bath_bar_show', {
    project_id: context.project_id,
    oid: context.oid,
    folder_name: folderName,
  });
}

/** 上报历史记录收藏或取消收藏动作。 */
export function trackRoboneoHistoryCollectAction(
  items: AssetItemView[],
  kind: HistoryActionKind,
  action: 'collect' | 'cancel',
): void {
  mtstatTrack(
    action === 'collect' ? 'roboneo_history_collect_click' : 'roboneo_history_collect_cancel',
    buildRoboneoHistoryCollectPayload(items, kind),
  );
}

/** 上报历史记录单条、批量或文件夹删除动作。 */
export function trackRoboneoHistoryContentDelete(items: AssetItemView[], kind: HistoryDeleteKind): void {
  mtstatTrack(
    'roboneo_history_content_delete',
    buildRoboneoHistoryContentDeletePayload(items, kind),
  );
}
